import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, GlobalStyles, Stack } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { type Place } from "@src/appV2/redesign/Onboarding/OnboardingFlowContainer/AddressStep/types";
import {
  BottomSheet,
  Input,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";

import { AddressAutocompleteInput } from "./AddressAutocompleteInput";

interface AddressInputBottomSheetProps {
  modalState: UseModalState;
  place?: Place | Partial<Place>;
  onPlaceChanged: (place: Place | Partial<Place>) => void;
  onContinue: () => void | Promise<void>;
  isContinueLoading: boolean;
  isContinueDisabled: boolean;
  onAddressInputChange: (value: string) => void;
  addressInput: string;
  addressInputErrorMessage: string;
  title?: string;
  buttonText?: string;
}

export function AddressInputBottomSheet(props: AddressInputBottomSheetProps) {
  const {
    modalState,
    place,
    onPlaceChanged,
    onContinue,
    isContinueLoading,
    isContinueDisabled,
    addressInput,
    onAddressInputChange,
    addressInputErrorMessage,
    title = "Enter home address",
    buttonText = "Continue",
  } = props;

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isContinueLoading}
            disabled={isContinueDisabled}
            onClick={onContinue}
          >
            {buttonText}
          </LoadingButton>
        </DialogFooter>
      }
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
      paperSx={{
        height: "100%",
      }}
    >
      <DialogContent>
        <Text semibold variant="h4" mb={8}>
          {title}
        </Text>
        <Stack spacing={6}>
          <Stack spacing={3}>
            {/* Override Google Maps autocomplete dropdown z-index to ensure it appears above the bottom sheet */}
            <GlobalStyles
              styles={{
                ".pac-container": {
                  zIndex: 1300,
                },
              }}
            />
            <Text semibold variant="body2" pl={1}>
              Street address
            </Text>
            <AddressAutocompleteInput
              addressInputValue={addressInput}
              errorMessage={addressInputErrorMessage}
              shouldFocusOnRender={!addressInput}
              disabled={isContinueLoading}
              onAddressInputChange={onAddressInputChange}
              onPlaceChanged={onPlaceChanged}
            />
          </Stack>

          {isDefined(place?.address) && !addressInputErrorMessage && (
            <Stack spacing={3}>
              <Text semibold variant="body2" pl={1}>
                Apartment, unit, or building number (optional)
              </Text>
              <Input
                fullWidth
                size="large"
                inputProps={{ "aria-label": "Apartment, unit, or building number" }}
                disabled={Boolean(place?.address?.subpremise) || isContinueLoading}
                value={place?.address?.subpremise?.trim() || place?.address?.line1?.trim() || ""}
                onChange={(event) => {
                  onPlaceChanged({
                    ...place,
                    address: {
                      ...place?.address,
                      line1: event.target.value,
                    },
                  });
                }}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
